/**
 * Module dependencies.
 */

import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { ComponentStatus, HealthComponent, useHealthStatus } from 'app/hooks/requests/use-health-status';
import { PageShell } from 'app/components/pages/landing/page-shell';
import React from 'react';

/**
 * Per-status presentation.
 */

const STATUS_UI: Record<ComponentStatus, { color: string; dot: string; label: string; tint: string }> = {
  degraded: { color: '#B45309', dot: '#F59E0B', label: 'Degraded', tint: 'rgba(245,158,11,0.12)' },
  down: { color: '#BE123C', dot: '#F43F5E', label: 'Down', tint: 'rgba(244,63,94,0.12)' },
  operational: { color: '#1B752B', dot: '#22A544', label: 'Operational', tint: 'rgba(34,165,68,0.12)' }
};

const OVERALL_UI: Record<ComponentStatus, { Icon: typeof CheckCircle2; color: string; tint: string; title: string }> = {
  degraded: { Icon: AlertTriangle, color: '#B45309', tint: 'rgba(245,158,11,0.10)', title: 'Some systems degraded' },
  down: { Icon: XCircle, color: '#BE123C', tint: 'rgba(244,63,94,0.10)', title: 'Major service outage' },
  operational: { Icon: CheckCircle2, color: '#1B752B', tint: 'rgba(34,165,68,0.12)', title: 'All systems operational' }
};

/**
 * Status pill.
 */

function StatusPill({ status }: { status: ComponentStatus }): JSX.Element {
  const ui = STATUS_UI[status];

  return (
    <span
      className={'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium'}
      style={{ backgroundColor: ui.tint, color: ui.color }}
    >
      <span className={'h-1.5 w-1.5 rounded-full'} style={{ backgroundColor: ui.dot }} />

      {ui.label}
    </span>
  );
}

/**
 * Component row.
 */

function ComponentRow({ component, first }: { component: HealthComponent; first: boolean }): JSX.Element {
  return (
    <div
      className={'flex items-center justify-between gap-4 px-6 py-4'}
      style={first ? undefined : { borderTop: '1px solid rgba(228,228,231,0.7)' }}
    >
      <div className={'min-w-0'}>
        <div className={'font-medium text-neutral-800'}>{component.name}</div>

        {typeof component.latencyMs === 'number' && component.status !== 'down' && (
          <div className={'mt-0.5 text-xs text-neutral-400'}>{`${component.latencyMs} ms response`}</div>
        )}
      </div>

      <StatusPill status={component.status} />
    </div>
  );
}

/**
 * Card style helper.
 */

const cardStyle = {
  backgroundImage: 'linear-gradient(160deg, rgba(255,255,255,0.85), rgba(255,255,255,0.6))',
  boxShadow: '0 30px 70px -40px rgba(10,10,11,0.3), inset 0 1px 0 0 rgba(255,255,255,0.9)'
} as const;

/**
 * Export `StatusPage` component.
 */

export function StatusPage(): JSX.Element {
  const { data, isError, isLoading } = useHealthStatus();
  const overall = data ? OVERALL_UI[data.status] : null;

  return (
    <PageShell>
      <main className={'mx-auto w-full max-w-3xl flex-1 px-6 py-16 sm:py-20'}>
        <h1 className={"font-['Outfit'] text-4xl font-semibold tracking-[-0.03em] sm:text-5xl"}>{'Status'}</h1>

        <p className={'mt-4 text-lg text-neutral-500'}>{'Live status of Contab and its services.'}</p>

        {/* Overall banner */}
        {isLoading && (
          <div
            className={'mt-8 flex items-center gap-3 rounded-2xl border border-white/60 p-6 backdrop-blur-xl'}
            style={cardStyle}
          >
            <Loader2 className={'h-5 w-5 animate-spin text-neutral-400'} />

            <span className={'text-neutral-500'}>{'Checking service status…'}</span>
          </div>
        )}

        {isError && !data && (
          <div
            className={'mt-8 flex items-center gap-3 rounded-2xl border border-white/60 p-6 backdrop-blur-xl'}
            style={cardStyle}
          >
            <XCircle className={'h-5 w-5'} style={{ color: '#BE123C' }} />

            <span className={'text-neutral-600'}>{'Unable to load status right now. Please try again shortly.'}</span>
          </div>
        )}

        {data && overall && (
          <>
            <div
              className={'mt-8 flex items-center gap-4 rounded-2xl border border-white/60 p-6 backdrop-blur-xl'}
              style={{
                backgroundImage: `linear-gradient(160deg, ${overall.tint}, rgba(255,255,255,0.6))`,
                boxShadow: '0 30px 70px -40px rgba(10,10,11,0.3), inset 0 1px 0 0 rgba(255,255,255,0.9)'
              }}
            >
              <overall.Icon className={'h-9 w-9 shrink-0'} strokeWidth={2} style={{ color: overall.color }} />

              <div>
                <div className={"font-['Outfit'] text-xl font-semibold tracking-[-0.02em]"}>{overall.title}</div>

                <div className={'mt-0.5 text-sm text-neutral-500'}>
                  {`Last checked at ${new Date(data.checkedAt).toLocaleTimeString()}`}
                </div>
              </div>
            </div>

            {/* Components */}
            <div className={'mt-6 rounded-2xl border border-white/60 backdrop-blur-xl'} style={cardStyle}>
              {data.components.map((component, index) => (
                <ComponentRow component={component} first={index === 0} key={component.key} />
              ))}
            </div>
          </>
        )}

        {/* Incidents */}
        <h2 className={"mt-12 font-['Outfit'] text-xl font-semibold tracking-[-0.02em]"}>{'Past incidents'}</h2>

        <div
          className={
            'mt-3 rounded-2xl border border-white/60 bg-white/60 p-6 text-[15px] text-neutral-500 backdrop-blur'
          }
        >
          {'No incidents reported in the last 90 days.'}
        </div>
      </main>
    </PageShell>
  );
}
